import { useState, useCallback } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Expand,
  Minimize2,
  TrendingUp,
  Lightbulb,
  Loader2,
  GitBranch,
} from "lucide-react";
import { adminApi } from "../../api/adminApi";
import { ContentDiffModal } from "./ContentDiffModal";
import toast from "react-hot-toast";

// Result structure from backend processRegenerateSection
interface RegenerationResult {
  sectionId: string;
  applied: boolean;
  comparison: {
    original: { title: string; content: string };
    regenerated: { title: string; content: string; differences: string[] };
    differences: string[];
  };
}

interface SectionAIToolbarProps {
  sectionId: string;
  sectionTitle: string;
  chapterId: string;
  onContentApplied?: () => void;
}

type AIAction = "expand" | "simplify" | "advanced" | "examples";

const ACTION_CONFIG: Record<
  AIAction,
  {
    label: string;
    icon: typeof Expand;
    style?: "concise" | "detailed" | "conversational";
    level?: "simpler" | "same" | "advanced";
    focus?: string;
  }
> = {
  expand: {
    label: "Expandir",
    icon: Expand,
    style: "detailed",
    level: "same",
  },
  simplify: {
    label: "Simplificar",
    icon: Minimize2,
    style: "concise",
    level: "simpler",
  },
  advanced: {
    label: "Más complejo",
    icon: TrendingUp,
    style: "detailed",
    level: "advanced",
  },
  examples: {
    label: "Ejemplos",
    icon: Lightbulb,
    style: "conversational",
    focus: "Añadir ejemplos prácticos y casos de uso reales",
  },
};

export function SectionAIToolbar({
  sectionId,
  sectionTitle,
  chapterId,
  onContentApplied,
}: SectionAIToolbarProps) {
  const queryClient = useQueryClient();
  const [activeAction, setActiveAction] = useState<AIAction | null>(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [regenerationResult, setRegenerationResult] =
    useState<RegenerationResult | null>(null);
  const [pollingTaskId, setPollingTaskId] = useState<string | null>(null);

  // Start regeneration mutation
  const regenerateMutation = useMutation({
    mutationFn: async (action: AIAction) => {
      const config = ACTION_CONFIG[action];
      const result = await adminApi.regenerateSection(sectionId, {
        style: config.style,
        level: config.level,
        focus: config.focus,
        preserveKeyPoints: action !== "expand",
        autoApply: false,
      });
      return { taskId: result.taskId, action };
    },
    onSuccess: ({ taskId }) => {
      setPollingTaskId(taskId);
      pollForResult(taskId);
    },
    onError: (error) => {
      setActiveAction(null);
      toast.error(
        error instanceof Error
          ? error.message
          : "Error al iniciar regeneración",
      );
    },
  });

  // Poll for task result
  const pollForResult = useCallback(async (taskId: string) => {
    const maxAttempts = 60; // 60 seconds max
    let attempts = 0;

    const poll = async () => {
      try {
        const result = await adminApi.getAITaskResult(taskId);

        if (result.status === "COMPLETED" && result.result) {
          setPollingTaskId(null);
          setRegenerationResult(result.result);
          setShowDiffModal(true);
          setActiveAction(null);
          toast.success("Contenido regenerado - revisa los cambios");
        } else if (result.status === "FAILED") {
          setPollingTaskId(null);
          setActiveAction(null);
          toast.error(result.error || "Error en la regeneración");
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 1000);
        } else {
          setPollingTaskId(null);
          setActiveAction(null);
          toast.error("Tiempo de espera agotado");
        }
      } catch (error) {
        setPollingTaskId(null);
        setActiveAction(null);
        toast.error("Error al obtener resultado");
      }
    };

    poll();
  }, []);

  // Apply regenerated content
  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!regenerationResult) throw new Error("No hay contenido para aplicar");
      return adminApi.applyRegeneratedSection(
        sectionId,
        regenerationResult.comparison.regenerated,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-chapter-sections", chapterId],
      });
      setShowDiffModal(false);
      setRegenerationResult(null);
      toast.success("Cambios aplicados");
      onContentApplied?.();
    },
    onError: () => {
      toast.error("Error al aplicar cambios");
    },
  });

  // Generate diagram mutation
  const [isGeneratingDiagram, setIsGeneratingDiagram] = useState(false);
  const diagramMutation = useMutation({
    mutationFn: () => adminApi.generateSectionDiagram(sectionId),
    onMutate: () => setIsGeneratingDiagram(true),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["section-diagrams", sectionId],
      });
      toast.success("Diagrama generado");
      onContentApplied?.();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : "Error al generar diagrama",
      );
    },
    onSettled: () => setIsGeneratingDiagram(false),
  });

  const handleAction = useCallback(
    (action: AIAction) => {
      setActiveAction(action);
      regenerateMutation.mutate(action);
    },
    [regenerateMutation],
  );

  const handleApply = useCallback(() => {
    applyMutation.mutate();
  }, [applyMutation]);

  const handleDiscard = useCallback(() => {
    setShowDiffModal(false);
    setRegenerationResult(null);
  }, []);

  const isLoading =
    activeAction !== null || pollingTaskId !== null || isGeneratingDiagram;

  return (
    <>
      <div className="flex items-center gap-1 flex-wrap">
        {(Object.keys(ACTION_CONFIG) as AIAction[]).map((action) => {
          const config = ACTION_CONFIG[action];
          const Icon = config.icon;
          const isActive = activeAction === action;

          return (
            <button
              key={action}
              type="button"
              className={`flex items-center gap-1 h-6 px-2 text-xs rounded transition-colors ${
                isActive
                  ? "bg-purple-200 text-purple-800"
                  : "bg-purple-50 text-purple-600 hover:bg-purple-100"
              }`}
              onClick={() => handleAction(action)}
              disabled={isLoading}
              title={config.label}
            >
              {isActive ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Icon className="h-3 w-3" />
              )}
              <span>{config.label}</span>
            </button>
          );
        })}

        {/* Diagram button */}
        <button
          type="button"
          className={`flex items-center gap-1 h-6 px-2 text-xs rounded transition-colors ${
            isGeneratingDiagram
              ? "bg-blue-200 text-blue-800"
              : "bg-blue-50 text-blue-600 hover:bg-blue-100"
          }`}
          onClick={() => diagramMutation.mutate()}
          disabled={isLoading}
          title="Generar diagrama para esta sección"
        >
          {isGeneratingDiagram ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : (
            <GitBranch className="h-3 w-3" />
          )}
          <span>Diagrama</span>
        </button>

        {isLoading && (
          <span className="text-xs text-purple-500 animate-pulse">...</span>
        )}
      </div>

      {/* Diff Modal */}
      {showDiffModal && regenerationResult && (
        <ContentDiffModal
          isOpen={showDiffModal}
          onClose={handleDiscard}
          original={regenerationResult.comparison.original}
          regenerated={regenerationResult.comparison.regenerated}
          differences={regenerationResult.comparison.differences}
          onApply={handleApply}
          onDiscard={handleDiscard}
          isApplying={applyMutation.isPending}
          sectionTitle={sectionTitle}
        />
      )}
    </>
  );
}
